/**
 * bankImport/types — Parser-Ausgabe-Typen für den Bank-Import (BR-1/BR-2).
 *
 * Pure types + eine Laufzeit-Invariantenprüfung. KEIN `zod` (siehe
 * changelog-parser.md „Abweichung: kein zod/papaparse" — `zod` ist in
 * `frontend-migrated` aktuell keine Dependency; golden rule „Input-Validation
 * an Modul-Grenzen (Zod/**Runtime-Checks**)" erlaubt Runtime-Checks explizit
 * als Alternative). `assertValidParsedStatementRow` übernimmt die in
 * architecture-bank-reconciliation.md §9.1 geforderte Zod-Schema-Rolle
 * (inkl. `direction`⟺Vorzeichen-Invariante).
 *
 * Architektur-Referenz: architecture-bank-reconciliation.md §6.4, §9.1, §9.4.
 *
 * @module bankImport/types
 */

import type { BankCsvColumnMap, BankCsvProfile } from '../../types/bankTypes';

/**
 * Eine geparste, normalisierte Kontoumsatz-Zeile — Zwischenformat zwischen
 * Parser (BR-1/BR-2) und Persistenz (BR-3). Entspricht `BankTransaction`
 * (`src/types/bankTypes.ts`) minus (`id`, `userId`, `accountId`, `importId`,
 * `source`, Match-Felder, `createdAt`/`createdBy`) — §9.1 der Architektur.
 *
 * `direction` ist bewusst Teil dieser Zeile (nicht nur später auf
 * `BankTransaction`): die „= BankTransaction minus (...)"-Formel in §9.1
 * schließt `direction` nicht aus der Differenzmenge aus, und die BR-1-DoD
 * verlangt explizit ein Zod-Schema „inkl. direction⟺Vorzeichen-Invariante"
 * — die Invariante muss also bereits auf dieser Zeile geprüft werden können.
 */
export interface ParsedStatementRow {
  /** ISO `YYYY-MM-DD`. */
  bookingDate: string;
  /** ISO `YYYY-MM-DD` oder `null`. */
  valueDate: string | null;
  /** SIGNIERT: >0 Gutschrift, <0 Belastung. Integer-Cents. */
  amountCents: number;
  /** Invariante: `direction === 'debit' ⟺ amountCents < 0` (§6.2). */
  direction: 'credit' | 'debit';
  /** `'EUR'` erwartet; andere Werte werden importiert, aber vom Matching ausgeschlossen. */
  currency: string;
  counterpartyName: string | null;
  /** Normalisiert Uppercase ohne Spaces. */
  counterpartyIban: string | null;
  counterpartyBic: string | null;
  /** Verwendungszweck (Ustrd gejoint / CSV-Spalten kombiniert); `''` erlaubt. */
  purpose: string;
  /** SEPA EndToEndId. */
  endToEndId: string | null;
  /** SEPA-Lastschrift `MndtId`. */
  mandateRef: string | null;
  /** SEPA `CdtrSchmeId` (falls vorhanden). */
  creditorId: string | null;
  /** CAMT `BkTxCd` Domain/Family-Kürzel oder CSV-Buchungstext. */
  bankTxCode: string | null;
  /** Bank-eigene eindeutige Referenz (bestes Dedup-Signal). */
  acctSvcrRef: string | null;
  /** Roh-Zeile (CSV) bzw. serialisiertes XML-Fragment, hart gekappt auf 2000 Zeichen. */
  rawSource: string;
}

/** Ergebnis eines Datei-Parses (CAMT.053 oder CSV). */
export interface ParsedStatement {
  /** CAMT: `Stmt/Acct/Id/IBAN`; CSV: immer `null` (CSV liefert keine Konto-IBAN). */
  accountIban: string | null;
  /** CAMT `Bal` mit `Tp/CdOrPrtry/Cd == 'OPBD'`; CSV: `null`. */
  openingBalanceCents: number | null;
  /** CAMT `Bal` mit `Tp/CdOrPrtry/Cd == 'CLBD'`; CSV: `null`. */
  closingBalanceCents: number | null;
  /** ISO `YYYY-MM-DD`; CSV: `null`. */
  closingBalanceDate: string | null;
  rows: ParsedStatementRow[];
  /** Zeilen-Fehler — Datei wird trotzdem importiert (`status:'partial'`, §9.4). */
  errors: Array<{ rawLine: string; reason: string }>;
  /**
   * MA-2 (architecture-mahn-automatik.md §6.3/§15, Coder-Review C-3): echte
   * Auszugsperiode aus `Stmt/FrToDt`, ausschließlich von `parseCamt053`
   * befüllt (`null`/`undefined` wenn das optionale Element fehlt). `parseBankCsv`
   * liefert diese Felder NICHT (CSV kennt keinen `FrToDt`-Header) — der
   * Import-Wizard kann sie stattdessen aus einer expliziten Nutzer-Bestätigung
   * heraus auf das `ParsedStatement`-Objekt mergen, bevor `importStatement`
   * aufgerufen wird. `bankApi.importStatement` schreibt sie 1:1 auf
   * `BankStatementImport.coverageFrom/coverageTo`.
   */
  coverageFrom?: string | null;
  /** Siehe {@link coverageFrom}. */
  coverageTo?: string | null;
}

/** Datei-Ebene-Fehlercodes (§9.4). */
export type BankImportErrorCode =
  | 'format_unknown'
  | 'xml_invalid'
  | 'no_transactions'
  | 'account_iban_mismatch'
  | 'file_too_large'
  | 'encoding_failed';

/**
 * Datei-Ebene-Fehler beim Bank-Import (§9.4). Wird von den Parsern bei
 * Struktur-Totalversagen geworfen — NIE eine stille Rückgabe (golden rules).
 *
 * `account_iban_mismatch` und `file_too_large` werden NICHT von dieser
 * BR-1/BR-2-Parser-Schicht geworfen (sie betreffen den Import-Wizard,
 * BR-3 — Konto-Auswahl bzw. Datei-Upload-Größencheck), sind aber Teil des
 * gemeinsamen Fehlercode-Vokabulars und daher hier mitdefiniert.
 */
export class BankImportError extends Error {
  readonly code: BankImportErrorCode;

  constructor(code: BankImportErrorCode, message?: string) {
    super(message ?? code);
    // TS/ES5-Downlevel-Falle (tsconfig target: es5): `extends Error` verliert
    // ohne diesen Fix die Prototype-Chain, sodass `instanceof BankImportError`
    // nach der Transpilation fehlschlagen kann.
    Object.setPrototypeOf(this, BankImportError.prototype);
    this.name = 'BankImportError';
    this.code = code;
  }
}

/**
 * Laufzeit-Invariantenprüfung für eine `ParsedStatementRow` (Ersatz für ein
 * echtes Zod-Schema, s. Modul-Kommentar oben). Wirft ein normales `Error`
 * (kein `BankImportError`) — der Aufrufer (Parser) entscheidet, ob ein
 * Verstoß eine einzelne Zeile (`ParsedStatement.errors[]`) oder die ganze
 * Datei betrifft.
 *
 * @throws {Error} Bei jeder Verletzung der Feld- oder Vorzeichen-Invarianten.
 */
export function assertValidParsedStatementRow(row: ParsedStatementRow): void {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(row.bookingDate)) {
    throw new Error(`ParsedStatementRow: ungültiges bookingDate "${row.bookingDate}" (erwartet YYYY-MM-DD).`);
  }
  if (row.valueDate !== null && !/^\d{4}-\d{2}-\d{2}$/.test(row.valueDate)) {
    throw new Error(`ParsedStatementRow: ungültiges valueDate "${row.valueDate}" (erwartet YYYY-MM-DD).`);
  }
  if (!Number.isInteger(row.amountCents)) {
    throw new Error(`ParsedStatementRow: amountCents muss ein Integer sein, war "${row.amountCents}".`);
  }
  if (row.direction !== 'credit' && row.direction !== 'debit') {
    throw new Error(`ParsedStatementRow: ungültige direction "${row.direction}".`);
  }
  if (row.direction === 'debit' && !(row.amountCents < 0)) {
    throw new Error(
      `ParsedStatementRow: direction==='debit' erfordert amountCents<0, war ${row.amountCents} (§6.2-Invariante).`
    );
  }
  if (row.direction === 'credit' && !(row.amountCents > 0)) {
    throw new Error(
      `ParsedStatementRow: direction==='credit' erfordert amountCents>0, war ${row.amountCents} (§6.2-Invariante).`
    );
  }
  if (!row.currency || row.currency.trim().length === 0) {
    throw new Error('ParsedStatementRow: currency darf nicht leer sein.');
  }
}

/**
 * Struktureller Ausschnitt aus `BankCsvProfile`, den `parseBankCsv` /
 * `findProfileByHeader` benötigen — sowohl persistierte User-Profile
 * (`BankCsvProfile`) als auch built-in-Profile (`BuiltinBankCsvProfile`,
 * ohne `id`/`userId`/`headerSignature`/`createdAt`/`createdBy`) erfüllen
 * diese Shape strukturell (§9.1: `parseBankCsv(text, profile: CsvProfileShape)`).
 */
export type CsvProfileShape = Pick<
  BankCsvProfile,
  'delimiter' | 'encoding' | 'decimalStyle' | 'dateFormat' | 'skipLeadingRows' | 'columnMap'
>;

// Re-export für Konsumenten, die nur `bankImport/types` importieren wollen.
export type { BankCsvColumnMap };
